// Org-tier devices list. One row per Grandstream phone in this org's
// inventory (imported from GDMS). Phase 2: read + import; per-device edits
// happen on the DeviceDetail route.
//
// Portal parity notes:
//   - Portal renders a live GDMS status pill (online/offline/unknown) sourced
//     from a synchronous cross-lookup against GDMS. The org-tier /devices API
//     doesn't surface those fields yet, so this pass only shows what the
//     device row itself carries (MAC, model, name, owner, location, updated).
//   - Portal has a scoped MAC-lookup "+ Add Device" form. Phase 2 seeds the
//     pool via the GDMS import button; ad-hoc adds come in Phase 3.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import Pagination, { usePagination } from '../components/shared/Pagination.jsx';
import { api, IMPORT_TIMEOUT_MS } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { canOperate, canAdmin } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import ListToolbar from '../components/shared/ListToolbar.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import ImportMenu from '../components/shared/ImportMenu.jsx';
import SortableHeader, { useSort } from '../components/shared/SortableHeader.jsx';
import { sharedList } from '../lib/liveQuery.js';

// "In inventory" rather than "Unassigned". Hardware we hold and have not deployed
// is stock, not a gap — and this is the tab an operator opens to find a spare to
// send out, which is a positive act rather than a cleanup.
//
// The key stays `pool` because it maps to the server's `?unassigned=1` filter; only
// the word an operator reads changes.
const DEVICE_FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'pool',     label: 'In inventory' },
];

function deviceStatusVariant(status) {
  switch (status) {
    case 'online':    return 'active';
    case 'offline':   return 'critical';
    // Its own colour, not another grey. Grey here means inactive, unknown or
    // disconnected — something wrong or unanswered — and a spare on the shelf is
    // none of those. Sharing grey with "unknown" made stock look like a device
    // GDMS could not report on.
    case 'inventory': return 'inventory';
    case 'unknown':
    default:          return 'unknown';
  }
}

// The server sends `inventory`; render it as two words.
function deviceStatusLabel(status) {
  return status === 'inventory' ? 'in inventory' : status;
}

// Modal to add a device to inventory by hand (vs. GDMS import). MAC is
// required; checking "Add to GDMS" (Grandstream hardware) requires the
// serial number — GDMS validates the (mac, sn) pair. The GDMS write fires
// only from this explicit operator action.
// `inventory` is the already-loaded device list from the page behind this modal —
// no extra request, and it's what lets the form say "you already have this one"
// while the operator is still typing rather than after they submit.
function AddDeviceModal({ onClose, onDone, inventory = [] }) {
  const navigate = useNavigate();
  const [deviceType, setDeviceType] = useState('grandstream');
  const [mac, setMac]     = useState('');
  const [fsan, setFsan]   = useState('');
  const [model, setModel] = useState('');
  const [name, setName]   = useState('');
  const [sn, setSn]       = useState('');
  const [toGdms, setToGdms] = useState(true);
  const [error, setError] = useState(null);
  // The created device, kept so the modal can REPORT rather than vanish. A toast
  // behind a closing dialog is the least visible place to put the one thing the
  // operator was waiting for — and when GDMS enrolls the device it also tells us
  // the model, which is worth showing where they're already looking.
  const [added, setAdded] = useState(null);

  const isCalix = deviceType === 'calix';

  const reset = () => {
    setMac(''); setFsan(''); setModel(''); setName(''); setSn('');
    setError(null); setAdded(null);
  };

  const mutation = useMutation({
    mutationFn: (body) => api.post('/devices', body),
    onSuccess: (res) => {
      setError(null);
      setAdded(res);
      // The list behind the modal refreshes now, so it's already correct whether
      // they close, open the device, or add another.
      onDone?.({ ok: true, quiet: true });
    },
    onError: (err) => setError(err?.body?.error || err?.message || 'Create failed'),
  });

  const macHex = mac.replace(/[^0-9a-fA-F]/g, '');

  // Already in inventory? Match format-agnostically (scanners and labels disagree
  // about colons) and on FSAN for Calix. Reported after a tech re-added a DP750 and
  // got GDMS's duplicate rejection dressed up as a MAC/serial mismatch.
  const duplicate = useMemo(() => {
    const hex = (x) => String(x || '').replace(/[^0-9a-f]/gi, '').toLowerCase();
    const serial = (d) => String(d.sn || d.fsan || '').trim().toUpperCase();
    if (isCalix) {
      const f = fsan.trim().toUpperCase();
      if (f.length < 4) return null;
      return inventory.find((d) => serial(d) === f) || null;
    }
    // MAC first — it's the key we store devices under. Then the SERIAL, because a
    // MAC typed with one character wrong would otherwise create a second row for a
    // phone we already have, and the serial is the thing printed next to it on the
    // label the tech is reading from.
    if (macHex.length >= 6) {
      const byMac = inventory.find((d) => hex(d.mac || d.mac_address) === macHex.toLowerCase());
      if (byMac) return byMac;
    }
    const typedSn = sn.trim().toUpperCase();
    if (typedSn.length >= 6) {
      return inventory.find((d) => serial(d) === typedSn) || null;
    }
    return null;
  }, [inventory, isCalix, fsan, macHex, sn]);
  // Which field raised it, so the notice can name the right one — "this serial is
  // already in inventory" when the MAC is new is the interesting case: it means one
  // of the two numbers is mistyped.
  const dupOnSerial = Boolean(duplicate) && !isCalix
    && String(duplicate.sn || '').trim().toUpperCase() === sn.trim().toUpperCase()
    && sn.trim().length >= 6;
  const dupMacMatches = Boolean(duplicate)
    && String(duplicate.mac || '').replace(/[^0-9a-f]/gi, '').toLowerCase() === macHex.toLowerCase();
  const canSubmit = isCalix
    ? fsan.trim().length > 0 && !mutation.isPending
    : macHex.length === 12 && (!toGdms || sn.trim()) && !mutation.isPending;

  // ---- Landed. Say what happened, and offer the two things worth doing next. ----
  if (added) {
    const g = added.gdms;
    const lk = added.calix_lookup;
    const ident = added.mac || added.fsan || mac || fsan;
    // GDMS refusing a device it ALREADY HAS is success, not failure — the route
    // reports that as ok with already_enrolled (see routes/devices.js).
    const vendor = isCalix
      ? (added.mac
        ? { tone: 'ok', text: `MAC ${added.mac} detected from Calix.` }
        : lk?.reason === 'calix_not_configured'
          ? { tone: 'warn', text: 'The MAC will fill in once the Calix integration is configured.' }
          : { tone: 'warn', text: 'Couldn’t reach Calix for the MAC — it fills in on the next health check.' })
      : g == null
        ? { tone: 'plain', text: 'Inventory only — not enrolled in GDMS.' }
        : g.ok
          ? { tone: 'ok', text: g.already_enrolled ? 'Already enrolled in GDMS.' : 'Enrolled in GDMS.' }
          : { tone: 'bad', text: g.error };
    const toneCls = {
      ok:    'border-green-200 bg-green-50 text-green-800',
      warn:  'border-amber-200 bg-amber-50 text-amber-900',
      bad:   'border-red-200 bg-red-50 text-red-700',
      plain: 'border-gray-200 bg-gray-50 text-gray-700',
    }[vendor.tone];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-lg font-bold text-green-700">
              ✓
            </span>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Device added</h3>
              <p className="mt-0.5 font-mono text-sm text-gray-700">{ident}</p>
            </div>
          </div>

          <dl className="mt-4 divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
            <div className="flex justify-between px-3 py-2">
              <dt className="text-gray-500">Model</dt>
              <dd className="font-medium text-gray-900">
                {added.model || <span className="text-gray-400">not known yet</span>}
                {/* The model came from the vendor, not from anything they typed —
                    worth saying, because the field was left blank on purpose. */}
                {g?.detected_model && (
                  <span className="ml-2 text-xs font-normal text-gray-500">detected</span>
                )}
              </dd>
            </div>
            {added.name && (
              <div className="flex justify-between px-3 py-2">
                <dt className="text-gray-500">Name</dt>
                <dd className="text-gray-900">{added.name}</dd>
              </div>
            )}
            <div className="flex justify-between px-3 py-2">
              <dt className="text-gray-500">Assigned to</dt>
              <dd className="text-gray-500">nobody yet — open the device to assign it</dd>
            </div>
          </dl>

          <p className={`mt-3 rounded-md border px-3 py-2 text-xs ${toneCls}`}>{vendor.text}</p>
          {g?.model_lookup_error && (
            <p className="mt-2 text-xs text-gray-500">
              Couldn’t read the model back from GDMS ({g.model_lookup_error}) — the next GDMS
              import fills it in.
            </p>
          )}

          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button
              onClick={reset}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Add another
            </button>
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            {/* Primary, because assigning it to a customer is what comes next and
                the detail page is where that happens. */}
            <button
              onClick={() => { onClose?.(); navigate(`/devices/${added.id}`); }}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Open device
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Add Device</h3>
        <p className="mt-1 text-xs text-gray-500">
          Adds a device to inventory. Grandstream hardware can be
          enrolled in GDMS; a Calix router is identified by its FSAN (serial) and
          its MAC is auto-detected from Calix.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setError(null);
            mutation.mutate(isCalix
              ? {
                  device_type: 'calix',
                  fsan: fsan.trim(),
                  model: model.trim() || undefined,
                  name: name.trim() || undefined,
                }
              : {
                  device_type: 'grandstream',
                  mac: macHex,
                  model: model.trim() || undefined,
                  name: name.trim() || undefined,
                  sn: sn.trim() || undefined,
                  add_to_gdms: toGdms,
                });
          }}
          className="mt-4 space-y-3"
        >
          <div>
            <label htmlFor="add-dev-type" className="block text-sm font-medium text-gray-700">Device type</label>
            <select id="add-dev-type"
              value={deviceType}
              onChange={(e) => { setDeviceType(e.target.value); setError(null); }}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="grandstream">Grandstream (IP phone / ATA)</option>
              <option value="calix">Calix (router / built-in ATA)</option>
            </select>
          </div>

          {isCalix ? (
            <div>
              <label htmlFor="add-dev-fsan" className="block text-sm font-medium text-gray-700">FSAN (serial)</label>
              <input id="add-dev-fsan"
                type="text" value={fsan} onChange={(e) => setFsan(e.target.value.toUpperCase())} required
                placeholder="CXNK009B44B5"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm uppercase focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-400">The MAC is looked up from Calix by FSAN and auto-filled.</p>
            </div>
          ) : (
            <div>
              <label htmlFor="add-dev-mac" className="block text-sm font-medium text-gray-700">MAC address</label>
              <input id="add-dev-mac"
                type="text" value={mac} onChange={(e) => setMac(e.target.value)} required
                placeholder="C0:74:AD:11:22:33"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}
          {/* Said while they're still typing, not after they submit. GDMS refuses a
              duplicate with a bare code, and reading that as a MAC/serial mismatch
              sent a tech back to re-scan a label that was correct. */}
          {duplicate && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-medium">
                This {isCalix ? 'serial' : (dupOnSerial && !dupMacMatches ? 'serial' : 'MAC')} is
                already in inventory.
              </p>
              <p className="mt-0.5">
                <span className="font-mono">{duplicate.mac || duplicate.fsan}</span>
                {duplicate.model && <> · {duplicate.model}</>}
                {' · '}
                {(duplicate.subscriberId ?? duplicate.subscriber_id)
                  ? `assigned to ${duplicate.subscriberName || duplicate.subscriber_name || 'a customer'}`
                  : 'in inventory'}
              </p>
              <button
                type="button"
                onClick={() => { onClose?.(); navigate(`/devices/${duplicate.id}`); }}
                className="mt-1.5 font-medium text-amber-900 underline hover:no-underline"
              >
                Open the existing device
              </button>
              {dupOnSerial && !dupMacMatches ? (
                // Serial matches, MAC doesn't: one of the two is mistyped, and
                // creating the row would give one physical phone two records.
                <p className="mt-1.5 font-medium text-amber-900">
                  The MAC you entered doesn&rsquo;t match that device, and a serial belongs to one
                  device — check both against the label. Saving will be refused.
                </p>
              ) : (
                <p className="mt-1.5 text-amber-800">
                  Adding it again won&rsquo;t create a second row — it updates this one, which is
                  worth doing if the first attempt didn&rsquo;t reach GDMS.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="add-dev-model" className="block text-sm font-medium text-gray-700">Model</label>
              <input id="add-dev-model"
                type="text" value={model} onChange={(e) => setModel(e.target.value)}
                placeholder="auto-detected if blank"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {/* Ticket #14: scanning the MAC left this empty with nothing to pick
                  from, and a typed guess ends up on the device row. The vendor
                  already knows — GDMS derives the model from the MAC + serial
                  pair — so leave it blank and let the enroll fill it in. */}
              <p className="mt-1 text-xs text-gray-400">
                {isCalix ? 'Looked up from Calix.' : 'Leave blank — read back from GDMS after enrolling.'}
              </p>
            </div>
            <div>
              <label htmlFor="add-dev-name" className="block text-sm font-medium text-gray-700">Name</label>
              <input id="add-dev-name"
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="optional label"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          {!isCalix && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={toGdms} onChange={(e) => setToGdms(e.target.checked)} />
              Add to GDMS (Grandstream device)
            </label>
          )}
          {!isCalix && toGdms && (
            <div>
              <label htmlFor="add-dev-sn" className="block text-sm font-medium text-gray-700">Serial number</label>
              <input id="add-dev-sn"
                type="text" value={sn} onChange={(e) => setSn(e.target.value)} required
                placeholder="SN from the device label"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-400">GDMS validates MAC + SN against Grandstream records.</p>
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button" onClick={onClose} disabled={mutation.isPending}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={!canSubmit}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Adding…' : 'Add device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DevicesList() {
  const { role } = useAuth();
  const canImport = canOperate(role);
  const canSyncProv = canAdmin(role);
  const [tab, setTab] = useState('all');
  const [importToast, setImportToast] = useState(null);
  const [addingDevice, setAddingDevice] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Row kebab: remove a device from inventory (type-delete confirm).
  const [removingDev, setRemovingDev] = useState(null);
  const removeDev = useMutation({
    mutationFn: (devId) => api.delete(`/devices/${devId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setRemovingDev(null);
    },
  });

  // ?unassigned=1 hits the pool. "assigned" gets filtered client-side because
  // the API doesn't expose an assigned-only filter yet — cheap enough for
  // Phase 2 device counts.
  const wantsPool = tab === 'pool';
  const sort = useSort({ col: 'mac', dir: 'asc' });
  const { data, isLoading, error } = useQuery({
    queryKey: ['devices', wantsPool ? 'pool' : 'all', sort.col, sort.dir],
    queryFn: () => api.get('/devices', {
      ...(wantsPool ? { unassigned: 1 } : {}),
      sort: sort.col,
      dir: sort.dir,
    }),
    ...sharedList(),
  });

  const rowsAll = useMemo(() => {
    if (Array.isArray(data))          return data;
    if (Array.isArray(data?.rows))    return data.rows;
    if (Array.isArray(data?.devices)) return data.devices;
    return [];
  }, [data]);

  const [search, setSearch] = useState('');
  // When the server-side data is scoped to 'pool' (unassigned=1), the
  // assigned + all buckets need a fresh fetch. Filter counts reflect
  // only the currently-fetched batch — good enough for typical device
  // pool sizes.
  const filterCounts = useMemo(() => ({
    all:        rowsAll.length,
    pool:       rowsAll.filter((d) => (d.subscriberId ?? d.subscriber_id) == null).length,
    assigned:   rowsAll.filter((d) => (d.subscriberId ?? d.subscriber_id) != null).length,
  }), [rowsAll]);
  const rows = useMemo(() => {
    let out = rowsAll;
    if (tab === 'assigned') out = out.filter((d) => (d.subscriberId ?? d.subscriber_id) != null);
    if (tab === 'pool')     out = out.filter((d) => (d.subscriberId ?? d.subscriber_id) == null);
    const s = search.trim().toLowerCase();
    if (s) {
      // MAC match is format-agnostic: strip every non-hex char from both the
      // stored MAC and the query so "00:0B:82", "00-0b-82", "000b82", and
      // "000b.82" all match the same device.
      const hex = (x) => x.replace(/[^0-9a-f]/gi, '').toLowerCase();
      const sHex = hex(s);
      out = out.filter((d) => {
        const mac    = String(d.mac || d.mac_address || '').toLowerCase();
        const model  = String(d.model || '').toLowerCase();
        const name   = String(d.name || '').toLowerCase();
        const subName = String(d.subscriberName || d.subscriber_name || '').toLowerCase();
        const fsan   = String(d.fsan || '').toLowerCase();
        const macHit = mac.includes(s) || (sHex.length >= 2 && hex(mac).includes(sHex));
        // FSAN is the identifier on a Calix unit; without it, searching for the
        // serial an operator is holding returns nothing.
        return macHit || fsan.includes(s) || model.includes(s) || name.includes(s) || subName.includes(s);
      });
    }
    return out;
  }, [rowsAll, tab, search]);

  const importMutation = useMutation({
    mutationFn: () => api.post('/devices/import', undefined, undefined, { timeoutMs: IMPORT_TIMEOUT_MS }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setImportToast({
        ok: true,
        message: `Imported ${res?.devices_upserted ?? 0} device${res?.devices_upserted === 1 ? '' : 's'} from GDMS`
          + (res?.errors?.length ? ` (${res.errors.length} error${res.errors.length === 1 ? '' : 's'})` : ''),
      });
      setTimeout(() => setImportToast(null), 5000);
    },
    onError: (err) => {
      setImportToast({
        ok: false,
        message: err?.body?.error || err?.message || 'Import failed',
      });
      setTimeout(() => setImportToast(null), 5000);
    },
  });

  const syncProvMutation = useMutation({
    mutationFn: () => api.post('/devices/provisioning-sync', undefined, undefined, { timeoutMs: IMPORT_TIMEOUT_MS }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setImportToast({
        ok: true,
        message: `Pulled GDMS provisioning for ${res?.synced ?? 0} of ${res?.total ?? 0} device${res?.total === 1 ? '' : 's'}`
          + (res?.skipped_no_mac ? `, ${res.skipped_no_mac} skipped (no MAC)` : '')
          + (res?.errors?.length ? `, ${res.errors.length} error${res.errors.length === 1 ? '' : 's'}` : ''),
      });
      setTimeout(() => setImportToast(null), 6000);
    },
    onError: (err) => {
      setImportToast({ ok: false, message: err?.body?.error || err?.message || 'Provisioning sync failed' });
      setTimeout(() => setImportToast(null), 6000);
    },
  });

  const { paged: pagedRows, pagination } = usePagination(rows);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
        <div className="flex items-center gap-3">
          {rows.length > 0 && (
            <span className="text-sm text-gray-500">
              {rows.length} device{rows.length === 1 ? '' : 's'}
            </span>
          )}
          {canSyncProv && (
            <button
              onClick={() => syncProvMutation.mutate()}
              disabled={syncProvMutation.isPending}
              title="Pull each device's live provisioning (account/line config) from GDMS into Verso."
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {syncProvMutation.isPending ? 'Syncing…' : 'Sync provisioning'}
            </button>
          )}
          {canImport && (
            <button
              onClick={() => setAddingDevice(true)}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Add device
            </button>
          )}
          {canImport && (
            <ImportMenu
              pending={importMutation.isPending}
              items={[
                { label: 'Import from GDMS', onClick: () => importMutation.mutate() },
              ]}
            />
          )}
        </div>
      </div>

      {importToast && (
        <div
          className={`rounded-md border px-4 py-2 text-sm ${
            importToast.ok
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {importToast.message}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by MAC, model, name, or subscriber…"
        filters={DEVICE_FILTERS}
        filterCounts={filterCounts}
        activeFilter={tab}
        onFilterChange={setTab}
      />

      <div className="rounded-lg border border-gray-200 bg-white">
        {isLoading ? (
          <LoadingSpinner message="Loading devices..." />
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">
              {tab === 'pool'     && 'Nothing in inventory — every device is owned by a subscriber.'}
              {tab === 'assigned' && 'No devices are currently assigned to a subscriber.'}
              {tab === 'all'      && 'No devices yet. Run an import from GDMS to seed the pool.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                <SortableHeader col="model"      sort={sort} label="Model" />
                <SortableHeader col="mac"        sort={sort} label="MAC" />
                <SortableHeader col="location"   sort={sort} label="Location" />
                <SortableHeader col="subscriber" sort={sort} label="Subscriber" />
                {canImport && <th className="px-2 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedRows.map((d) => {
                const id    = d.id;
                const dType = d.deviceType || d.device_type || 'grandstream';
                // Grandstream is MAC-keyed; Calix shows its FSAN (serial) when
                // no MAC has been detected yet.
                const ident = d.mac || d.fsan || '—';
                const model = d.model || <span className="text-gray-300">—</span>;
                const subId = d.subscriberId ?? d.subscriber_id;
                const subName = d.subscriberName || d.subscriber_name;
                const locId = d.resolvedLocationId ?? d.locationId ?? d.location_id;
                const locLabel = d.locationLabel || d.location_label;
                const locAddr  = d.locationAddress || d.location_address;
                const locDerived = d.locationDerived;
                // Server derives status from the row's import source: GDMS
                // for gdms-sourced rows, subscriber assignment for the pool,
                // "unknown" when the upstream can't be reached. See
                // routes/devices.js enrichWithLiveStatus.
                const status = d.status || 'unknown';
                const statusVariant = deviceStatusVariant(status);
                return (
                  <tr key={id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <StatusBadge status={statusVariant} label={deviceStatusLabel(status)} />
                    </td>
                    <td className="px-4 py-2 text-gray-700">{model}</td>
                    <td className="px-4 py-2 font-mono">
                      <Link to={`/devices/${id}`} className="text-blue-600 hover:underline">
                        {ident}
                      </Link>
                      {dType === 'calix' && (
                        <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 font-sans">Calix</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {locId && (locLabel || locAddr) ? (
                        <Link to={`/locations/${locId}`} className="text-blue-600 hover:underline">
                          {locLabel || locAddr}
                          {locLabel && locAddr && (
                            <span className="block text-xs text-gray-400">{locAddr}</span>
                          )}
                          {locDerived && (
                            <span className="block text-xs text-gray-400 italic">via subscriber</span>
                          )}
                        </Link>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {subId ? (
                        <Link to={`/subscribers/${subId}`} className="text-blue-600 hover:underline">
                          {subName || `#${subId}`}
                        </Link>
                      ) : (
                        // Same words as the badge and the filter tab, so all
                        // three agree on what this row is.
                        <span className="text-indigo-700">In inventory</span>
                      )}
                    </td>
                    {canImport && (
                      <td className="px-2 py-2 text-right">
                        <ActionMenu
                          title={`Actions for ${ident}`}
                          items={[
                            { label: 'View details', onClick: () => navigate(`/devices/${id}`) },
                            ...(subId ? [{ label: 'View subscriber', onClick: () => navigate(`/subscribers/${subId}`) }] : []),
                            { divider: true },
                            { label: 'Remove from inventory', danger: true, onClick: () => { removeDev.reset(); setRemovingDev(d); } },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination {...pagination} />
      </div>

      {removingDev && (
        <ConfirmDeleteDialog
          destructive={false}
          title="Remove device"
          body={`Remove ${removingDev.mac || `device #${removingDev.id}`} from inventory? This soft-deletes the Verso row and detaches it from any subscriber/location; the physical device in GDMS is untouched.`}
          confirmLabel="Remove"
          isPending={removeDev.isPending}
          error={removeDev.isError ? (removeDev.error?.body?.error || removeDev.error?.message) : null}
          onCancel={() => setRemovingDev(null)}
          onConfirm={() => removeDev.mutate(removingDev.id)}
        />
      )}

      {addingDevice && (
        <AddDeviceModal
          inventory={rowsAll}
          onClose={() => setAddingDevice(false)}
          onDone={(t) => {
            queryClient.invalidateQueries({ queryKey: ['devices'] });
            // The modal reports the outcome itself now, so a toast would just be a
            // second, quieter copy of it. Keep the channel for anything that still
            // wants one (the GDMS import).
            if (t && !t.quiet) {
              setImportToast(t);
              setTimeout(() => setImportToast(null), 6000);
            }
          }}
        />
      )}
    </div>
  );
}
